import * as DialogPrimitive from '@radix-ui/react-dialog';
import { SVG_ATTRIBUTES, SVG_ELEMENTS, type ViewChild, type ViewNode } from '@deskworks/view-runtime';
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, Loader2, Search, X } from 'lucide-react';
import { createContext, createElement, Fragment, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { AssetImage } from '@/components/files/asset-image';
import { Markdown as MarkdownText } from '@/components/markdown';
import { Button as UiButton } from '@/components/ui/button';
import { Dialog as UiDialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input as UiInput } from '@/components/ui/input';
import { Select as UiSelect, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch as UiSwitch } from '@/components/ui/switch';
import { Textarea as UiTextarea } from '@/components/ui/textarea';
import { intlLocale, t } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { ICON_MAP } from './kit-icons';

/**
 * Draws a view's element tree with Deskworks's own components
 * (docs/features/views.md). Props come from sealed code, so they are
 * treated as plain data: only known components, known SVG elements and
 * attributes, no HTML, no styles, no URLs (links only to Deskworks pages,
 * pictures only from the workspace's own files).
 * Function props arrive as { __fn: id } and are sent back to the view as
 * events. Inputs keep what is typed themselves and report it as events, so
 * typing never waits for the engine. Buttons with `action` run the view's
 * declared action through the host, which asks the person first where the
 * action says so.
 */

type OnEvent = (handler: string, arg?: unknown) => void;

/** What the page around the view offers its elements (view-host.tsx). */
export interface KitHost {
  runAction: (name: string, row: string | null, input?: Record<string, unknown>) => Promise<unknown>;
  actionLabel: (name: string) => string;
  navigate: (to: string) => void;
}
export const KitHostContext = createContext<KitHost | null>(null);

/** Inputs with a `name` inside a <Form> put their value here; the form hands all of them to onSubmit. */
const FormContext = createContext<{ set: (name: string, value: unknown) => void; unset: (name: string) => void } | null>(null);
type P = Record<string, unknown>;

const TONES = ['default', 'muted', 'primary', 'success', 'warning', 'danger', 'info', 'accent'];
const SERIES = ['primary', 'info', 'success', 'warning', 'accent', 'danger'];
const tone = (v: unknown, fallback = 'primary') => `var(--tone-${TONES.includes(String(v)) ? String(v) : fallback})`;

const TEXT_TONE: Record<string, string> = {
  default: 'text-foreground',
  muted: 'text-muted-foreground',
  primary: 'text-primary',
  success: 'text-emerald-600 dark:text-emerald-400',
  warning: 'text-amber-600 dark:text-amber-400',
  danger: 'text-red-600 dark:text-red-400',
  info: 'text-sky-600 dark:text-sky-400',
  accent: 'text-violet-600 dark:text-violet-400',
};
const BADGE_TONE: Record<string, string> = {
  default: 'border-zinc-200 bg-zinc-100 text-zinc-700 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300',
  muted: 'border-zinc-200 bg-transparent text-zinc-500 dark:border-zinc-700 dark:text-zinc-400',
  primary: 'border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-900 dark:bg-indigo-950/60 dark:text-indigo-300',
  success: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/50 dark:text-emerald-300',
  warning: 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-950/50 dark:text-amber-300',
  danger: 'border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/50 dark:text-red-300',
  info: 'border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-900 dark:bg-sky-950/60 dark:text-sky-300',
  accent: 'border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-900 dark:bg-violet-950/50 dark:text-violet-300',
};
const GAP = ['gap-0', 'gap-1', 'gap-2', 'gap-3', 'gap-4', 'gap-6', 'gap-8'];
const COLS = ['', 'grid-cols-1', 'sm:grid-cols-2', 'sm:grid-cols-2 lg:grid-cols-3', 'sm:grid-cols-2 lg:grid-cols-4', 'sm:grid-cols-3 lg:grid-cols-5', 'sm:grid-cols-3 lg:grid-cols-6'];
const ALIGN: Record<string, string> = { start: 'items-start', center: 'items-center', end: 'items-end', stretch: 'items-stretch' };
const JUSTIFY: Record<string, string> = { start: 'justify-start', center: 'justify-center', end: 'justify-end', between: 'justify-between' };
const EASE = 'cubic-bezier(.2,.8,.2,1)';

const str = (v: unknown, fallback = '') => (v === null || v === undefined ? fallback : typeof v === 'object' ? JSON.stringify(v) : String(v));
const num = (v: unknown) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const arr = (v: unknown): P[] => (Array.isArray(v) ? (v.filter((x) => x && typeof x === 'object') as P[]) : []);
const gap = (v: unknown, d = 3) => GAP[Math.min(Math.max(Math.round(num(v ?? d)), 0), GAP.length - 1)] ?? 'gap-3';
const fn = (v: unknown): string | null => (v && typeof v === 'object' && '__fn' in (v as P) ? String((v as P).__fn) : null);
const childKey = (c: ViewChild, i: number) => (typeof c === 'object' && c && c.props?.key !== undefined && c.props.key !== null ? `k${str(c.props.key)}` : i);
const DESKWORKS_PATH = /^\/w\/[^/]+\//;

const ISO_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

function fmt(value: unknown, format?: unknown) {
  if (value === null || value === undefined) return '—';
  // Timestamps from Postgres arrive as ISO strings: show them in local time.
  if (typeof value === 'string' && ISO_TIME.test(value)) {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) return format === 'date' ? d.toLocaleDateString(intlLocale) : d.toLocaleString(intlLocale, { dateStyle: 'medium', timeStyle: 'short' });
  }
  if (format === 'percent') return `${(Math.round(num(value) * 10) / 10).toLocaleString(intlLocale)}%`;
  if (format === 'date') return str(value).slice(0, 10);
  if (format === 'number' || typeof value === 'number') {
    const n = num(value);
    return n.toLocaleString(intlLocale, { maximumFractionDigits: Math.abs(n) >= 100 ? 0 : 2 });
  }
  return str(value);
}

// ---------------------------------------------------------------- animation helpers

/** Counts from the previous value to the new one. */
function AnimatedNumber({ value, format }: { value: number; format?: unknown }) {
  const [shown, setShown] = useState(value);
  const from = useRef(value);
  useEffect(() => {
    const start = performance.now();
    const a = from.current;
    let frame = 0;
    const step = (now: number) => {
      const k = Math.min((now - start) / 700, 1);
      const eased = 1 - Math.pow(1 - k, 3);
      setShown(a + (value - a) * eased);
      if (k < 1) frame = requestAnimationFrame(step);
      else from.current = value;
    };
    frame = requestAnimationFrame(step);
    return () => {
      cancelAnimationFrame(frame);
      from.current = value;
    };
  }, [value]);
  const rounded = Number.isInteger(value) ? Math.round(shown) : Math.round(shown * 10) / 10;
  return <>{fmt(rounded, format === 'percent' ? 'percent' : 'number')}</>;
}

/** Briefly highlights an element when `value` changes (new data arrived). */
function useFlash(value: unknown) {
  const [flash, setFlash] = useState(false);
  const first = useRef(true);
  const key = JSON.stringify(value);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    setFlash(true);
    const timer = window.setTimeout(() => setFlash(false), 900);
    return () => window.clearTimeout(timer);
  }, [key]);
  return flash;
}

/** FLIP: children with data-flip-id glide from their old position to the new one. */
function useFlip(container: React.RefObject<HTMLElement | null>, deps: unknown) {
  const rects = useRef(new Map<string, DOMRect>());
  useLayoutEffect(() => {
    const el = container.current;
    if (!el) return;
    const items = [...el.querySelectorAll<HTMLElement>('[data-flip-id]')];
    for (const item of items) {
      const id = item.dataset.flipId!;
      const before = rects.current.get(id);
      const after = item.getBoundingClientRect();
      if (before && (before.left !== after.left || before.top !== after.top)) {
        item.animate(
          [{ transform: `translate(${before.left - after.left}px, ${before.top - after.top}px)` }, { transform: 'translate(0, 0)' }],
          { duration: 500, easing: EASE },
        );
      }
    }
    rects.current = new Map(items.map((i) => [i.dataset.flipId!, i.getBoundingClientRect()]));
  }, [deps, container]);
}

// ---------------------------------------------------------------- components

function Metric({ p }: { p: P }) {
  const value = p.value;
  const flash = useFlash(value);
  const delta = p.delta === undefined || p.delta === null ? null : num(p.delta);
  return (
    <div className={cn('rounded-lg border bg-card px-4 py-3 transition-colors duration-700', flash && 'bg-primary/5')}>
      <div className="text-xs font-medium text-muted-foreground">{str(p.label)}</div>
      <div className={cn('mt-1 flex items-baseline gap-1.5 text-2xl font-semibold tracking-tight tabular-nums', TEXT_TONE[str(p.tone)] ?? '')}>
        {typeof value === 'number' || (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value))) ? (
          <AnimatedNumber value={num(value)} format={p.format} />
        ) : (
          str(value, '—')
        )}
        {p.unit !== undefined && <span className="text-sm font-normal text-muted-foreground">{str(p.unit)}</span>}
        {delta !== null && (
          <span className={cn('ml-1 text-xs font-medium', delta > 0 ? TEXT_TONE.success : delta < 0 ? TEXT_TONE.danger : TEXT_TONE.muted)}>
            {delta > 0 ? '▲' : delta < 0 ? '▼' : '•'} {fmt(Math.abs(delta), p.format)}
          </span>
        )}
      </div>
      {p.hint !== undefined && <div className="mt-0.5 text-xs text-muted-foreground">{str(p.hint)}</div>}
    </div>
  );
}

function Progress({ p }: { p: P }) {
  const max = num(p.max ?? 100) || 100;
  const pct = Math.min(Math.max((num(p.value) / max) * 100, 0), 100);
  return (
    <div className="grid gap-1">
      {p.label !== undefined && (
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>{str(p.label)}</span>
          <span className="tabular-nums">{Math.round(pct)}%</span>
        </div>
      )}
      <div className="h-2 overflow-hidden rounded-full bg-muted">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: tone(p.tone), transition: `width 700ms ${EASE}` }} />
      </div>
    </div>
  );
}

function BarChart({ p, onEvent }: { p: P; onEvent: OnEvent }) {
  const rows = arr(p.data).slice(0, 100);
  const x = str(p.x, 'key');
  const y = str(p.y, 'count');
  const max = Math.max(1, ...rows.map((r) => num(r[y])));
  const height = Math.min(Math.max(num(p.height) || 180, 80), 600);
  const color = (r: P) => tone(p.toneKey ? r[str(p.toneKey)] : p.tone);
  const click = fn(p.onClick);
  if (!rows.length) return <div className="py-6 text-center text-sm text-muted-foreground">{t('No data')}</div>;
  if (p.horizontal) {
    return (
      <div className="grid gap-1.5">
        {rows.map((r, i) => (
          <button key={str(r[x], String(i))} type="button" disabled={click === null} onClick={() => click !== null && onEvent(click, r)} className="grid grid-cols-[minmax(80px,30%)_1fr_auto] items-center gap-2 text-left text-xs disabled:cursor-default">
            <span className="truncate text-muted-foreground">{str(r[x])}</span>
            <span className="h-4 overflow-hidden rounded bg-muted/60">
              <span className="block h-full rounded" style={{ width: `${(num(r[y]) / max) * 100}%`, background: color(r), transition: `width 700ms ${EASE}` }} />
            </span>
            <span className="tabular-nums">{fmt(r[y])}</span>
          </button>
        ))}
      </div>
    );
  }
  return (
    <div>
      <div className="flex items-end gap-2" style={{ height }}>
        {rows.map((r, i) => (
          <button
            key={str(r[x], String(i))}
            type="button"
            title={`${str(r[x])}: ${fmt(r[y])}`}
            disabled={click === null}
            onClick={() => click !== null && onEvent(click, r)}
            className="flex h-full min-w-0 flex-1 items-end justify-center disabled:cursor-default"
          >
            <span
              className="block w-full max-w-24 rounded-t-md animate-in fade-in"
              style={{ height: `${Math.max((num(r[y]) / max) * 100, 0.5)}%`, background: color(r), transition: `height 700ms ${EASE}, background 300ms` }}
            />
          </button>
        ))}
      </div>
      <div className="mt-1.5 flex gap-2">
        {rows.map((r, i) => (
          <span key={str(r[x], String(i))} className="min-w-0 flex-1 truncate text-center text-[11px] text-muted-foreground">
            {str(r[x])} <span className="text-foreground tabular-nums">{fmt(r[y])}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function LineChart({ p, area }: { p: P; area?: boolean }) {
  const rows = arr(p.data).slice(0, 500);
  const x = str(p.x, 'key');
  const keys = (Array.isArray(p.y) ? p.y : [p.y ?? 'value']).map((k) => str(k)).slice(0, 6);
  const height = Math.min(Math.max(num(p.height) || 180, 80), 600);
  const W = 600;
  const values = rows.flatMap((r) => keys.map((k) => num(r[k])));
  const max = Math.max(1, ...values);
  const min = Math.min(0, ...values);
  if (rows.length < 2) return <div className="py-6 text-center text-sm text-muted-foreground">{t('Not enough data')}</div>;
  const px = (i: number) => (i / (rows.length - 1)) * W;
  const py = (v: number) => height - ((v - min) / (max - min || 1)) * (height - 8) - 4;
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${height}`} className="w-full overflow-visible" preserveAspectRatio="none" style={{ height }}>
        {keys.map((k, s) => {
          const line = rows.map((r, i) => `${i ? 'L' : 'M'}${px(i).toFixed(1)},${py(num(r[k])).toFixed(1)}`).join(' ');
          const color = tone(SERIES[s % SERIES.length]);
          return (
            <g key={k}>
              {area && <path d={`${line} L${W},${height} L0,${height} Z`} fill={color} opacity={0.12} style={{ transition: `d 700ms ${EASE}` }} />}
              <path d={line} fill="none" stroke={color} strokeWidth={2} vectorEffect="non-scaling-stroke" strokeLinejoin="round" style={{ transition: `d 700ms ${EASE}` }} />
            </g>
          );
        })}
      </svg>
      <div className="mt-1 flex justify-between text-[11px] text-muted-foreground">
        <span>{str(rows[0][x])}</span>
        {keys.length > 1 && (
          <span className="flex gap-3">
            {keys.map((k, s) => (
              <span key={k} className="inline-flex items-center gap-1">
                <span className="size-2 rounded-full" style={{ background: tone(SERIES[s % SERIES.length]) }} /> {k}
              </span>
            ))}
          </span>
        )}
        <span>{str(rows[rows.length - 1][x])}</span>
      </div>
    </div>
  );
}

function PieChart({ p }: { p: P }) {
  const rows = arr(p.data).slice(0, 12);
  const label = str(p.label, 'key');
  const value = str(p.value, 'count');
  const total = rows.reduce((s, r) => s + Math.max(num(r[value]), 0), 0) || 1;
  const R = 15.9155; // circumference 100
  let offset = 25;
  return (
    <div className="flex flex-wrap items-center gap-6">
      <svg viewBox="0 0 42 42" className="size-36">
        <circle cx="21" cy="21" r={R} fill="none" stroke="var(--muted)" strokeWidth={p.donut === false ? 31.8 : 6} />
        {rows.map((r, i) => {
          const share = (Math.max(num(r[value]), 0) / total) * 100;
          const el = (
            <circle
              key={str(r[label], String(i))}
              cx="21"
              cy="21"
              r={R}
              fill="none"
              stroke={tone(SERIES[i % SERIES.length])}
              strokeWidth={p.donut === false ? 31.8 : 6}
              strokeDasharray={`${share} ${100 - share}`}
              strokeDashoffset={offset}
              style={{ transition: `stroke-dasharray 700ms ${EASE}, stroke-dashoffset 700ms ${EASE}` }}
            />
          );
          offset -= share;
          return el;
        })}
      </svg>
      <div className="grid gap-1 text-xs">
        {rows.map((r, i) => (
          <div key={str(r[label], String(i))} className="flex items-center gap-2">
            <span className="size-2.5 rounded-sm" style={{ background: tone(SERIES[i % SERIES.length]) }} />
            <span className="min-w-0 truncate">{str(r[label])}</span>
            <span className="ml-auto pl-3 text-muted-foreground tabular-nums">{Math.round((Math.max(num(r[value]), 0) / total) * 100)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Table({ p, onEvent }: { p: P; onEvent: OnEvent }) {
  const rows = arr(p.data).slice(0, 5000);
  const rowKey = str(p.rowKey, 'id');
  const cols = (arr(p.columns).length ? arr(p.columns) : Object.keys(rows[0] ?? {}).map((key): P => ({ key }))).map((c) => ({
    key: str(c.key),
    label: str(c.label ?? c.key),
    right: c.align === 'right',
    format: c.format,
  }));
  const click = fn(p.onRowClick);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<{ key: string; dir: 1 | -1 } | null>(null);
  const [page, setPage] = useState(0);
  const pageSize = Math.min(Math.max(Math.round(num(p.pageSize)) || 0, 0), 500);
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    let out = q ? rows.filter((r) => cols.some((c) => str(r[c.key]).toLowerCase().includes(q))) : rows;
    if (sort) {
      out = [...out].sort((a, b) => {
        const x = a[sort.key];
        const y = b[sort.key];
        if (x === y) return 0;
        if (x === null || x === undefined) return 1;
        if (y === null || y === undefined) return -1;
        const nx = Number(x);
        const ny = Number(y);
        const cmp = Number.isFinite(nx) && Number.isFinite(ny) && typeof x !== 'boolean' ? nx - ny : str(x).localeCompare(str(y));
        return cmp * sort.dir;
      });
    }
    return out;
    // rows/cols come from props; their JSON is the dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(p.data), JSON.stringify(p.columns), query, sort]);
  const pages = pageSize ? Math.max(Math.ceil(shown.length / pageSize), 1) : 1;
  const current = Math.min(page, pages - 1);
  const visible = pageSize ? shown.slice(current * pageSize, (current + 1) * pageSize) : shown.slice(0, 500);
  const sortable = p.sortable !== false;
  return (
    <div className="grid gap-2">
      {p.search !== undefined && p.search !== false && (
        <div className="relative max-w-xs">
          <Search className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <UiInput
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setPage(0);
            }}
            placeholder={typeof p.search === 'string' ? p.search : t('Search')}
            className="h-8 pl-8 text-[13px]"
          />
        </div>
      )}
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-left text-[13px]">
          <thead className="bg-muted/50 text-xs text-muted-foreground">
            <tr>
              {cols.map((c) => (
                <th key={c.key} className={cn('px-3 py-2 font-medium whitespace-nowrap', c.right && 'text-right')}>
                  {sortable ? (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 hover:text-foreground"
                      onClick={() => setSort((s) => (s?.key !== c.key ? { key: c.key, dir: 1 } : s.dir === 1 ? { key: c.key, dir: -1 } : null))}
                    >
                      {c.label}
                      {sort?.key === c.key && (sort.dir === 1 ? <ArrowUp className="size-3" /> : <ArrowDown className="size-3" />)}
                    </button>
                  ) : (
                    c.label
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y">
            {visible.map((r, i) => (
              <tr
                key={str(r[rowKey] ?? i)}
                onClick={() => click !== null && onEvent(click, r)}
                className={cn(
                  'animate-in fade-in duration-500',
                  click !== null && 'cursor-pointer hover:bg-muted/40',
                  p.selected !== undefined && p.selected !== null && str(r[rowKey]) === str(p.selected) && 'bg-primary/5',
                )}
              >
                {cols.map((c) => (
                  <td key={c.key} className={cn('px-3 py-1.5', c.right && 'text-right tabular-nums')}>
                    {fmt(r[c.key], c.format)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {!visible.length && <div className="px-3 py-5 text-center text-sm text-muted-foreground">{query ? t('Nothing found') : t('No rows')}</div>}
      </div>
      {pageSize > 0 && pages > 1 && (
        <div className="flex items-center justify-end gap-2 text-xs text-muted-foreground">
          <span className="tabular-nums">
            {t('{from}–{to} of {total}', {
              from: (current * pageSize + 1).toLocaleString(intlLocale),
              to: Math.min((current + 1) * pageSize, shown.length).toLocaleString(intlLocale),
              total: shown.length.toLocaleString(intlLocale),
            })}
          </span>
          <UiButton variant="outline" size="icon-sm" disabled={current === 0} onClick={() => setPage(current - 1)} aria-label={t('Previous page')}>
            <ChevronLeft />
          </UiButton>
          <UiButton variant="outline" size="icon-sm" disabled={current >= pages - 1} onClick={() => setPage(current + 1)} aria-label={t('Next page')}>
            <ChevronRight />
          </UiButton>
        </div>
      )}
    </div>
  );
}

function ListView({ p, onEvent }: { p: P; onEvent: OnEvent }) {
  const items = arr(p.items).slice(0, 300);
  const click = fn(p.onClick);
  const ref = useRef<HTMLDivElement>(null);
  useFlip(ref, items.map((i) => str(i.id)).join('|'));
  return (
    <div ref={ref} className="divide-y rounded-lg border">
      {items.map((it, i) => (
        <button
          key={str(it.id ?? i)}
          data-flip-id={str(it.id ?? i)}
          type="button"
          disabled={click === null}
          onClick={() => click !== null && onEvent(click, it)}
          className={cn(
            'flex w-full items-center gap-3 px-3 py-2 text-left animate-in fade-in slide-in-from-bottom-1 duration-500 hover:bg-muted/30 disabled:cursor-default disabled:hover:bg-transparent',
            p.selected !== undefined && p.selected !== null && str(it.id) === str(p.selected) && 'bg-primary/5 hover:bg-primary/10',
          )}
        >
          {it.icon !== undefined ? (
            <KitIcon name={it.icon} tone={it.tone} className="size-4 shrink-0" />
          ) : (
            it.tone !== undefined && <span className="size-2 shrink-0 rounded-full" style={{ background: tone(it.tone) }} />
          )}
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm">{str(it.title)}</span>
            {it.subtitle !== undefined && <span className="block truncate text-xs text-muted-foreground">{str(it.subtitle)}</span>}
          </span>
          {it.meta !== undefined && <span className="shrink-0 text-xs text-muted-foreground tabular-nums">{fmt(it.meta)}</span>}
          {it.badge !== undefined && <span className={cn('rounded-md border px-1.5 py-0.5 text-[11px]', BADGE_TONE[str(it.badgeTone ?? it.tone, 'default')] ?? BADGE_TONE.default)}>{str(it.badge)}</span>}
        </button>
      ))}
      {!items.length && <div className="px-3 py-5 text-center text-sm text-muted-foreground">{t('Nothing here')}</div>}
    </div>
  );
}

function Board({ p, onEvent }: { p: P; onEvent: OnEvent }) {
  const columns = arr(p.columns).slice(0, 12);
  const items = arr(p.items).slice(0, 500);
  const click = fn(p.onClick);
  const ref = useRef<HTMLDivElement>(null);
  useFlip(ref, items.map((i) => `${str(i.id)}:${str(i.column)}`).join('|'));
  return (
    <div ref={ref} className="flex gap-3 overflow-x-auto pb-1">
      {columns.map((c) => {
        const inColumn = items.filter((i) => str(i.column) === str(c.key));
        return (
          <div key={str(c.key)} className="min-w-[180px] flex-1 rounded-lg bg-muted/40 p-2">
            <div className="mb-2 flex items-center gap-2 px-1 text-xs font-medium">
              <span className="size-2 rounded-full" style={{ background: tone(c.tone, 'muted') }} />
              {str(c.title ?? c.key)}
              <span className="ml-auto text-muted-foreground tabular-nums">{inColumn.length}</span>
            </div>
            <div className="grid gap-2">
              {inColumn.map((it, i) => (
                <button
                  key={str(it.id ?? i)}
                  data-flip-id={str(it.id ?? i)}
                  type="button"
                  disabled={click === null}
                  onClick={() => click !== null && onEvent(click, it)}
                  className="rounded-md border bg-card px-3 py-2 text-left shadow-xs animate-in fade-in zoom-in-95 duration-300 disabled:cursor-default"
                  style={{ borderLeft: `3px solid ${tone(it.tone ?? c.tone, 'muted')}` }}
                >
                  <div className="text-sm">{str(it.title)}</div>
                  {it.subtitle !== undefined && <div className="mt-0.5 text-xs text-muted-foreground">{str(it.subtitle)}</div>}
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Timeline({ p }: { p: P }) {
  const items = arr(p.items).slice(0, 300);
  return (
    <ol className="relative ml-2 border-l pl-5">
      {items.map((it, i) => (
        <li key={str(it.id ?? i)} className="relative pb-4 animate-in fade-in slide-in-from-left-1 duration-500 last:pb-0">
          <span className="absolute top-1.5 -left-[25px] size-2.5 rounded-full ring-4 ring-background" style={{ background: tone(it.tone) }} />
          <div className="text-[11px] text-muted-foreground">{str(it.time).replace('T', ' ').slice(0, 16)}</div>
          <div className="text-sm">{str(it.title)}</div>
          {it.subtitle !== undefined && <div className="text-xs text-muted-foreground">{str(it.subtitle)}</div>}
        </li>
      ))}
    </ol>
  );
}

function Tabs({ p, onEvent }: { p: P; onEvent: OnEvent }) {
  const change = fn(p.onChange);
  return (
    <div className="inline-flex rounded-md border p-0.5">
      {arr(p.tabs).map((tab) => (
        <button
          key={str(tab.key)}
          type="button"
          onClick={() => change !== null && onEvent(change, tab.key)}
          className={cn('rounded px-2.5 py-1 text-xs transition-colors', str(tab.key) === str(p.value) ? 'bg-secondary font-medium' : 'text-muted-foreground hover:text-foreground')}
        >
          {str(tab.label ?? tab.key)}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- icons, text, overlays

function KitIcon({ name, tone: iconTone, className }: { name: unknown; tone?: unknown; className?: string }) {
  const Icon = ICON_MAP[str(name) as keyof typeof ICON_MAP];
  if (!Icon) return null;
  return <Icon className={cn('size-4 shrink-0', className)} style={iconTone !== undefined ? { color: tone(iconTone) } : undefined} />;
}

const SIDE_WIDTH: Record<string, string> = {
  sm: 'md:grid-cols-[240px_minmax(0,1fr)]',
  md: 'md:grid-cols-[320px_minmax(0,1fr)]',
  lg: 'md:grid-cols-[420px_minmax(0,1fr)]',
  half: 'md:grid-cols-2',
};
const DIALOG_WIDTH: Record<string, string> = { sm: 'sm:max-w-sm', md: 'sm:max-w-lg', lg: 'sm:max-w-2xl', xl: 'sm:max-w-4xl' };
const DRAWER_WIDTH: Record<string, string> = { sm: 'sm:max-w-sm', md: 'sm:max-w-lg', lg: 'sm:max-w-2xl', xl: 'sm:max-w-4xl' };

function KitDialog({ p, onEvent, children }: { p: P; onEvent: OnEvent; children: ReactNode }) {
  const close = fn(p.onClose);
  return (
    <UiDialog open={!!p.open} onOpenChange={(open) => !open && close !== null && onEvent(close)}>
      <DialogContent className={DIALOG_WIDTH[str(p.size, 'md')] ?? DIALOG_WIDTH.md}>
        {(p.title !== undefined || p.description !== undefined) && (
          <DialogHeader>
            <DialogTitle>{str(p.title)}</DialogTitle>
            {p.description !== undefined && <DialogDescription>{str(p.description)}</DialogDescription>}
          </DialogHeader>
        )}
        <div className="flex min-w-0 flex-col gap-3">{children}</div>
      </DialogContent>
    </UiDialog>
  );
}

function KitDrawer({ p, onEvent, children }: { p: P; onEvent: OnEvent; children: ReactNode }) {
  const close = fn(p.onClose);
  return (
    <DialogPrimitive.Root open={!!p.open} onOpenChange={(open) => !open && close !== null && onEvent(close)}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/30 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0" />
        <DialogPrimitive.Content
          className={cn(
            'fixed inset-y-0 right-0 z-50 flex w-full flex-col border-l bg-background shadow-xl',
            'data-[state=open]:animate-in data-[state=open]:slide-in-from-right data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right',
            DRAWER_WIDTH[str(p.size, 'md')] ?? DRAWER_WIDTH.md,
          )}
          aria-describedby={undefined}
        >
          <div className="flex items-start gap-3 border-b px-5 py-4">
            <div className="min-w-0 flex-1">
              <DialogPrimitive.Title className="text-base font-semibold">{str(p.title)}</DialogPrimitive.Title>
              {p.subtitle !== undefined && <div className="text-xs text-muted-foreground">{str(p.subtitle)}</div>}
            </div>
            <DialogPrimitive.Close className="rounded-sm text-muted-foreground opacity-70 hover:opacity-100" aria-label={t('Close')}>
              <X className="size-4" />
            </DialogPrimitive.Close>
          </div>
          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-5 py-4">{children}</div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function KitLink({ p, onEvent, children }: { p: P; onEvent: OnEvent; children: ReactNode }) {
  const host = useContext(KitHostContext);
  const click = fn(p.onClick);
  const to = typeof p.to === 'string' && DESKWORKS_PATH.test(p.to) ? p.to : null;
  const className = 'text-primary underline-offset-2 hover:underline';
  if (to) {
    return (
      <a
        href={to}
        className={className}
        onClick={(e) => {
          if (e.metaKey || e.ctrlKey || e.shiftKey) return;
          e.preventDefault();
          host?.navigate(to);
        }}
      >
        {children}
      </a>
    );
  }
  return (
    <button type="button" className={cn(className, 'w-fit text-left')} onClick={() => click !== null && onEvent(click)}>
      {children}
    </button>
  );
}

function Details({ p }: { p: P }) {
  const items = arr(p.items).slice(0, 100);
  return (
    <dl className={cn('grid gap-x-4 gap-y-2 text-sm', p.columns === 2 ? 'sm:grid-cols-[auto_1fr_auto_1fr]' : 'grid-cols-[minmax(90px,auto)_1fr]')}>
      {items.map((it, i) => (
        <Fragment key={str(it.label, String(i))}>
          <dt className="text-muted-foreground">{str(it.label)}</dt>
          <dd className={cn('min-w-0 break-words', TEXT_TONE[str(it.tone)] ?? '')}>{fmt(it.value, it.format)}</dd>
        </Fragment>
      ))}
    </dl>
  );
}

const CALLOUT_ICON: Record<string, string> = { success: 'success', warning: 'warning', danger: 'alert', info: 'info', primary: 'info', accent: 'sparkles' };

function Callout({ p, children }: { p: P; children: ReactNode }) {
  const calloutTone = str(p.tone, 'info');
  return (
    <div className={cn('flex gap-3 rounded-lg border px-3 py-2.5 text-sm', BADGE_TONE[calloutTone] ?? BADGE_TONE.info)}>
      <KitIcon name={p.icon ?? CALLOUT_ICON[calloutTone] ?? 'info'} className="mt-0.5" />
      <div className="min-w-0 flex-1">
        {p.title !== undefined && <div className="font-medium">{str(p.title)}</div>}
        <div className="flex flex-col gap-1">{children}</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- inputs

/**
 * What the person typed stays here; the view hears it as onChange events. A value the view sends back that we
 * reported ourselves is an echo and changes nothing (so fast typing never jumps back); any other value from the view
 * (e.g. '' after saving) replaces what is shown.
 */
function useFieldValue<T>(p: P, initial: T, onEvent: OnEvent, convert: (v: T) => unknown = (v) => v) {
  const form = useContext(FormContext);
  const name = typeof p.name === 'string' ? p.name : null;
  const [value, setValue] = useState<T>(p.value !== undefined && p.value !== null ? (p.value as T) : p.defaultValue !== undefined ? (p.defaultValue as T) : initial);
  const sent = useRef<string[]>([]);
  const incoming = JSON.stringify(p.value ?? null);
  useEffect(() => {
    if (p.value === undefined) return;
    const at = sent.current.indexOf(incoming);
    if (at >= 0) sent.current = sent.current.slice(at + 1);
    else setValue((p.value ?? initial) as T);
    // only when the view's value changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incoming]);
  useEffect(() => {
    if (!form || !name) return;
    form.set(name, convert(value));
  }, [form, name, value, convert]);
  useEffect(() => () => void (form && name && form.unset(name)), [form, name]);
  const change = fn(p.onChange);
  const update = (next: T) => {
    setValue(next);
    if (change !== null) {
      const out = convert(next);
      sent.current = [...sent.current.slice(-20), JSON.stringify(out ?? null)];
      onEvent(change, out);
    }
  };
  return [value, update] as const;
}

function FieldLabel({ p, children }: { p: P; children: ReactNode }) {
  if (p.label === undefined && p.hint === undefined) return <>{children}</>;
  return (
    <label className="grid gap-1.5 text-sm">
      {p.label !== undefined && (
        <span className="font-medium">
          {str(p.label)}
          {p.required ? <span className="text-red-500"> *</span> : null}
        </span>
      )}
      {children}
      {p.hint !== undefined && <span className="text-xs text-muted-foreground">{str(p.hint)}</span>}
    </label>
  );
}

const INPUT_TYPES = new Set(['text', 'number', 'email', 'search', 'date', 'datetime-local', 'time', 'url', 'tel', 'password', 'color']);
const asNumber = (v: string) => (v.trim() === '' ? null : Number(v));
const asText = (v: string) => v;

function KitInput({ p, onEvent }: { p: P; onEvent: OnEvent }) {
  const type = INPUT_TYPES.has(str(p.type)) ? str(p.type) : 'text';
  const [value, update] = useFieldValue<string>({ ...p, value: p.value === undefined || p.value === null ? p.value : str(p.value) }, '', onEvent, type === 'number' ? asNumber : asText);
  const submit = fn(p.onSubmit);
  return (
    <FieldLabel p={p}>
      <UiInput
        type={type}
        value={value}
        placeholder={str(p.placeholder)}
        required={!!p.required}
        disabled={!!p.disabled}
        min={p.min as number | undefined}
        max={p.max as number | undefined}
        onChange={(e) => update(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && submit !== null) {
            e.preventDefault();
            onEvent(submit, type === 'number' ? asNumber(value) : value);
          }
        }}
        className="h-9"
      />
    </FieldLabel>
  );
}

function KitTextarea({ p, onEvent }: { p: P; onEvent: OnEvent }) {
  const [value, update] = useFieldValue<string>({ ...p, value: p.value === undefined || p.value === null ? p.value : str(p.value) }, '', onEvent);
  return (
    <FieldLabel p={p}>
      <UiTextarea
        value={value}
        placeholder={str(p.placeholder)}
        required={!!p.required}
        disabled={!!p.disabled}
        rows={Math.min(Math.max(Math.round(num(p.rows)) || 4, 2), 40)}
        onChange={(e) => update(e.target.value)}
      />
    </FieldLabel>
  );
}

function KitSelect({ p, onEvent }: { p: P; onEvent: OnEvent }) {
  const options = (Array.isArray(p.options) ? p.options : []).slice(0, 500).map((o) =>
    o && typeof o === 'object' ? { value: (o as P).value, label: str((o as P).label ?? (o as P).value) } : { value: o, label: str(o) },
  );
  const byKey = new Map(options.map((o) => [str(o.value), o.value]));
  const [value, update] = useFieldValue<unknown>(p, null, onEvent);
  const current = value === null || value === undefined || value === '' ? undefined : str(value);
  return (
    <FieldLabel p={p}>
      <UiSelect value={current ?? ''} onValueChange={(v) => update(byKey.has(v) ? byKey.get(v) : v)} disabled={!!p.disabled}>
        <SelectTrigger className="h-9 w-full min-w-40">
          <SelectValue placeholder={str(p.placeholder, t('Choose …'))} />
        </SelectTrigger>
        <SelectContent>
          {options
            .filter((o) => str(o.value) !== '')
            .map((o) => (
              <SelectItem key={str(o.value)} value={str(o.value)}>
                {o.label}
              </SelectItem>
            ))}
        </SelectContent>
      </UiSelect>
    </FieldLabel>
  );
}

function KitCheck({ p, onEvent, toggle }: { p: P; onEvent: OnEvent; toggle?: boolean }) {
  const [checked, update] = useFieldValue<boolean>({ ...p, value: p.checked ?? p.value }, false, onEvent);
  return (
    <label className={cn('inline-flex w-fit items-center gap-2 text-sm', p.disabled ? 'opacity-60' : 'cursor-pointer')}>
      {toggle ? (
        <UiSwitch checked={!!checked} disabled={!!p.disabled} onCheckedChange={(v) => update(v)} />
      ) : (
        <input type="checkbox" className="size-4 rounded accent-[var(--primary)]" checked={!!checked} disabled={!!p.disabled} onChange={(e) => update(e.target.checked)} />
      )}
      {p.label !== undefined && <span>{str(p.label)}</span>}
    </label>
  );
}

function KitForm({ p, onEvent, children }: { p: P; onEvent: OnEvent; children: ReactNode }) {
  const values = useRef<Record<string, unknown>>({});
  const ctx = useMemo(
    () => ({
      set: (name: string, value: unknown) => void (values.current[name] = value),
      unset: (name: string) => void delete values.current[name],
    }),
    [],
  );
  const submit = fn(p.onSubmit);
  const cancel = fn(p.onCancel);
  const host = useContext(KitHostContext);
  const [busy, setBusy] = useState(false);
  return (
    <FormContext.Provider value={ctx}>
      <form
        className="flex flex-col gap-3"
        onSubmit={async (e) => {
          e.preventDefault();
          const all = { ...values.current };
          // A form with an action runs it with its fields (the comment field becomes the comment); onSubmit hears the result.
          if (typeof p.action === 'string' && host) {
            setBusy(true);
            try {
              const result = await host.runAction(p.action, p.row === undefined || p.row === null ? null : str(p.row), all);
              if (submit !== null) onEvent(submit, { values: all, result });
            } catch {
              /* the host shows why */
            } finally {
              setBusy(false);
            }
            return;
          }
          if (submit !== null) onEvent(submit, all);
        }}
      >
        {children}
        {p.submitLabel !== false && (
          <div className="flex items-center gap-2 pt-1">
            <UiButton type="submit" size="sm" loading={busy}>
              {str(p.submitLabel, typeof p.action === 'string' && host ? host.actionLabel(p.action) : t('Save'))}
            </UiButton>
            {cancel !== null && (
              <UiButton type="button" variant="ghost" size="sm" onClick={() => onEvent(cancel)}>
                {str(p.cancelLabel, t('Cancel'))}
              </UiButton>
            )}
          </div>
        )}
      </form>
    </FormContext.Provider>
  );
}

const BUTTON_VARIANT: Record<string, 'default' | 'outline' | 'ghost' | 'destructive' | 'secondary' | 'link'> = {
  primary: 'default',
  outline: 'outline',
  ghost: 'ghost',
  danger: 'destructive',
  secondary: 'secondary',
  link: 'link',
};

function KitButton({ p, onEvent, children }: { p: P; onEvent: OnEvent; children: ReactNode[] }) {
  const host = useContext(KitHostContext);
  const click = fn(p.onClick);
  const [busy, setBusy] = useState(false);
  const action = typeof p.action === 'string' ? p.action : null;
  const run = async () => {
    if (action && host) {
      setBusy(true);
      try {
        const result = await host.runAction(action, p.row === undefined || p.row === null ? null : str(p.row), (p.input && typeof p.input === 'object' ? p.input : {}) as Record<string, unknown>);
        // onClick hears how it went (the action's result).
        if (click !== null) onEvent(click, result);
      } catch {
        /* the host shows why */
      } finally {
        setBusy(false);
      }
      return;
    }
    if (click !== null) onEvent(click);
  };
  const label = children.length ? children : action && host ? host.actionLabel(action) : null;
  const icon = p.icon !== undefined ? <KitIcon name={p.icon} /> : null;
  const size = p.size === 'lg' ? 'lg' : p.size === 'xs' ? 'xs' : p.size === 'md' ? 'default' : 'sm';
  const variant = BUTTON_VARIANT[str(p.variant)];
  // Legacy look: a tone colours the button; without tone or variant it is an outline button.
  if (!variant && p.tone && p.tone !== 'default') {
    return (
      <button
        type="button"
        onClick={run}
        disabled={!!p.disabled || busy || !!p.loading}
        className="inline-flex h-8 w-fit items-center gap-1.5 rounded-md px-3 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50"
        style={{ background: tone(p.tone) }}
      >
        {busy || p.loading ? <Loader2 className="size-4 animate-spin" /> : icon}
        {label}
      </button>
    );
  }
  return (
    <UiButton type="button" variant={variant ?? 'outline'} size={size} className={cn('w-fit', !!p.full && 'w-full')} loading={busy || !!p.loading} disabled={!!p.disabled} onClick={run}>
      {!(busy || p.loading) && icon}
      {label}
    </UiButton>
  );
}

const SVG_OK = new Set<string>(SVG_ELEMENTS);
const SVG_ATTR_OK = new Set<string>(SVG_ATTRIBUTES);

function svgNode(node: ViewChild, onEvent: OnEvent, key: number): ReactNode {
  if (typeof node === 'string' || typeof node === 'number') return node;
  if (!SVG_OK.has(node.type)) return null;
  const props: Record<string, unknown> = { key };
  for (const [k, v] of Object.entries(node.props)) {
    if (k === 'onClick' && fn(v) !== null) props.onClick = () => onEvent(fn(v)!, node.props.id);
    else if (SVG_ATTR_OK.has(k) && (typeof v === 'string' || typeof v === 'number')) {
      props[k] = (k === 'fill' || k === 'stroke') && TONES.includes(String(v)) ? tone(v) : v;
    }
  }
  if (node.type !== 'text' && node.type !== 'tspan') props.style = { transition: `all 600ms ${EASE}` };
  return createElement(node.type, props, ...node.children.map((c, i) => svgNode(c, onEvent, i)));
}

// ---------------------------------------------------------------- the tree

export function KitRender({ node, onEvent }: { node: ViewChild; onEvent: OnEvent }): ReactNode {
  if (typeof node === 'string' || typeof node === 'number') return node;
  if (!node || typeof node !== 'object') return null;
  const p = node.props ?? {};
  const kids = () => node.children.map((c, i) => <KitRender key={childKey(c, i)} node={c} onEvent={onEvent} />);
  switch (node.type) {
    case '__fragment':
      return <Fragment>{kids()}</Fragment>;
    case 'Stack':
      return <div className={cn('flex flex-col', gap(p.gap, 4), ALIGN[str(p.align)])}>{kids()}</div>;
    case 'Row':
      return <div className={cn('flex', gap(p.gap), ALIGN[str(p.align)] ?? 'items-center', JUSTIFY[str(p.justify)], p.wrap !== false && 'flex-wrap')}>{kids()}</div>;
    case 'Grid':
      return <div className={cn('grid', gap(p.gap, 4), COLS[Math.min(Math.max(num(p.columns ?? 2), 1), COLS.length - 1)])}>{kids()}</div>;
    case 'Card':
      return (
        <div className="rounded-lg border bg-card p-4" style={p.tone ? { borderTop: `3px solid ${tone(p.tone)}` } : undefined}>
          {(p.title !== undefined || p.subtitle !== undefined) && (
            <div className="mb-3">
              {p.title !== undefined && <div className="text-sm font-semibold">{str(p.title)}</div>}
              {p.subtitle !== undefined && <div className="text-xs text-muted-foreground">{str(p.subtitle)}</div>}
            </div>
          )}
          <div className="flex flex-col gap-3">{kids()}</div>
        </div>
      );
    case 'Divider':
      return <hr className="border-border" />;
    case 'Heading': {
      const level = Math.min(Math.max(num(p.level ?? 2), 1), 4);
      return <div className={cn('font-semibold tracking-tight', ['', 'text-2xl', 'text-lg', 'text-base', 'text-sm'][level])}>{kids()}</div>;
    }
    case 'Text':
      return (
        <p
          className={cn(
            TEXT_TONE[str(p.tone)] ?? '',
            { sm: 'text-xs', md: 'text-sm', lg: 'text-base' }[str(p.size, 'md')] ?? 'text-sm',
            { medium: 'font-medium', bold: 'font-semibold' }[str(p.weight)] ?? '',
          )}
        >
          {kids()}
        </p>
      );
    case 'Badge':
      return <span className={cn('inline-flex w-fit items-center rounded-md border px-2 py-0.5 text-xs font-medium', BADGE_TONE[str(p.tone, 'default')] ?? BADGE_TONE.default)}>{kids()}</span>;
    case 'Metric':
      return <Metric p={p} />;
    case 'Progress':
      return <Progress p={p} />;
    case 'BarChart':
      return <BarChart p={p} onEvent={onEvent} />;
    case 'LineChart':
      return <LineChart p={p} />;
    case 'AreaChart':
      return <LineChart p={p} area />;
    case 'PieChart':
      return <PieChart p={p} />;
    case 'Table':
      return <Table p={p} onEvent={onEvent} />;
    case 'List':
      return <ListView p={p} onEvent={onEvent} />;
    case 'Board':
      return <Board p={p} onEvent={onEvent} />;
    case 'Timeline':
      return <Timeline p={p} />;
    case 'Tabs':
      return <Tabs p={p} onEvent={onEvent} />;
    case 'Button':
      return <KitButton p={p} onEvent={onEvent} children={kids()} />;
    case 'Split': {
      const [side, ...main] = node.children;
      return (
        <div className={cn('grid grid-cols-1', gap(p.gap, 4), SIDE_WIDTH[str(p.side, 'md')] ?? SIDE_WIDTH.md)}>
          <div className="flex min-w-0 flex-col gap-3">{side !== undefined && <KitRender node={side} onEvent={onEvent} />}</div>
          <div className="flex min-w-0 flex-col gap-4">
            {main.map((c, i) => (
              <KitRender key={childKey(c, i)} node={c} onEvent={onEvent} />
            ))}
          </div>
        </div>
      );
    }
    case 'Scroll':
      return (
        <div className={cn('flex flex-col overflow-y-auto pr-1', gap(p.gap, 3))} style={{ maxHeight: Math.min(Math.max(num(p.height) || 400, 80), 3000) }}>
          {kids()}
        </div>
      );
    case 'Dialog':
      return (
        <KitDialog p={p} onEvent={onEvent}>
          {kids()}
        </KitDialog>
      );
    case 'Drawer':
      return (
        <KitDrawer p={p} onEvent={onEvent}>
          {kids()}
        </KitDrawer>
      );
    case 'Markdown':
      return <MarkdownText text={str(p.text ?? node.children.filter((c) => typeof c !== 'object').join(''))} />;
    case 'Image':
      return (
        <AssetImage
          src={p.src}
          alt={p.alt === undefined ? undefined : str(p.alt)}
          caption={p.caption === undefined ? undefined : str(p.caption)}
          height={p.height === undefined ? undefined : num(p.height) || undefined}
          fit={p.fit === 'cover' ? 'cover' : 'contain'}
        />
      );
    case 'Link':
      return (
        <KitLink p={p} onEvent={onEvent}>
          {kids()}
        </KitLink>
      );
    case 'Icon':
      return <KitIcon name={p.name} tone={p.tone} className={p.size === 'lg' ? 'size-6' : p.size === 'sm' ? 'size-3.5' : undefined} />;
    case 'Callout':
      return <Callout p={p}>{kids()}</Callout>;
    case 'Details':
      return <Details p={p} />;
    case 'Empty':
      return (
        <div className="flex flex-col items-center gap-1.5 rounded-lg border border-dashed px-4 py-8 text-center">
          <KitIcon name={p.icon ?? 'box'} className="size-6 text-muted-foreground" />
          <div className="text-sm font-medium">{str(p.title, t('Nothing here yet'))}</div>
          {p.hint !== undefined && <div className="max-w-sm text-xs text-muted-foreground">{str(p.hint)}</div>}
          {kids()}
        </div>
      );
    case 'Spinner':
      return (
        <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> {p.label !== undefined && str(p.label)}
        </div>
      );
    case 'Input':
      return <KitInput p={p} onEvent={onEvent} />;
    case 'Textarea':
      return <KitTextarea p={p} onEvent={onEvent} />;
    case 'Select':
      return <KitSelect p={p} onEvent={onEvent} />;
    case 'Checkbox':
      return <KitCheck p={p} onEvent={onEvent} />;
    case 'Switch':
      return <KitCheck p={p} onEvent={onEvent} toggle />;
    case 'Form':
      return (
        <KitForm p={p} onEvent={onEvent}>
          {kids()}
        </KitForm>
      );
    case 'Svg': {
      const w = Math.min(Math.max(num(p.width) || 300, 10), 2000);
      const h = Math.min(Math.max(num(p.height) || 150, 10), 2000);
      return (
        <svg viewBox={typeof p.viewBox === 'string' ? p.viewBox : `0 0 ${w} ${h}`} className="w-full overflow-visible" style={{ maxWidth: w, height: 'auto' }}>
          {node.children.map((c, i) => svgNode(c, onEvent, i))}
        </svg>
      );
    }
    default:
      return <span className="rounded bg-muted px-1 text-[11px] text-muted-foreground">{t('unknown element “{type}”', { type: node.type })}</span>;
  }
}

export type { ViewNode };
